import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'src/common/axiosInstance';

// AUTO-WA-39: the WhatsApp ↔ Auto Appy AI connection, shared by Bots (which
// bots answer) and Settings → AI connection (key, pause, test mode).
// GET/PUT /workspaces/{id}/ai-connection; the backend never returns the key.

export const emptyAiView = { data: null, channel: null, bots: [], legacy_bots: [] };

export const aiFailure = (error) => Object.values(error?.response?.data?.errors || {}).flat().join(' ')
  || error?.response?.data?.message || 'Unable to save the AI connection.';

const AI_BASE = 'https://ai.autoappy.com';

// AI links always carry workspace=<organisation>: AI never guesses one and shows
// "Choose an organisation" without it. The AI tenant is the central Auto Appy
// organisation id, so it falls back to the signed-in organisation before a bot
// is connected.
export const aiTenant = (view, currentUser) => view?.data?.tenant_id || currentUser?.current_workspace?.id || '';
const aiLink = (path, tenant, extra = {}) => {
  const query = new URLSearchParams({ ...(tenant ? { workspace: tenant } : {}), ...extra }).toString();
  return `${AI_BASE}${path}${query ? `?${query}` : ''}`;
};
export const aiUrl = (tenant, extra = {}) => aiLink('/automation', tenant, extra);
export const aiBotUrl = (tenant, bot) => (bot.adapter === 'static_flow'
  ? aiLink('/static-flows', tenant, { bot: bot.id })
  : aiUrl(tenant, { tab: 'bots', bot: bot.id }));
export const aiLibraryUrl = (tenant) => aiLink('/bot-library', tenant);
export const aiGuidedUrl = (tenant) => aiLink('/static-flows', tenant);

export function aiStatus(view) {
  if (!view?.data) return { tone: 'draft', label: 'Not connected', key: 'none' };
  if (!view.data.enabled) return { tone: 'paused', label: 'AI replies paused', key: 'paused' };
  if (view.error) return { tone: 'warning', label: 'AI status unavailable', key: 'unknown' };
  if (!view.channel?.active) return { tone: 'paused', label: 'Paused in Auto Appy AI', key: 'channel' };
  if (view.data.test_recipient) return { tone: 'info', label: `Test mode · only ${view.data.test_recipient}`, key: 'test' };
  return { tone: 'live', label: 'AI replies on', key: 'on' };
}

export default function useAiConnection(workspaceId) {
  const [view, setView] = useState(emptyAiView);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [canManage, setCanManage] = useState(true);
  const generation = useRef(0);

  const load = useCallback(async () => {
    const current = ++generation.current;
    setLoading(true); setError(''); setCanManage(true);
    if (!workspaceId) { setView(emptyAiView); setLoading(false); return; }
    try {
      const response = await axios.get(`/workspaces/${workspaceId}/ai-connection`);
      if (generation.current !== current) return;
      setView({ ...emptyAiView, ...response.data });
    } catch (failure) {
      if (generation.current !== current) return;
      setCanManage(false);
      setError(failure?.response?.status === 403 ? 'An organisation owner or administrator manages AI bots.' : 'Unable to load the AI connection. Refresh to try again.');
    } finally {
      if (generation.current === current) setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    setView(emptyAiView);
    load();
    const reference = generation;
    return () => { reference.current += 1; };
  }, [load]);

  // Resolves with the new view; rejects with a readable message. A reply for a
  // workspace the page has since left is ignored.
  const save = useCallback(async (payload) => {
    const current = generation.current;
    try {
      const response = await axios.put(`/workspaces/${workspaceId}/ai-connection`, payload);
      if (generation.current !== current) return null;
      setView((old) => ({ ...old, ...response.data }));
      return response.data;
    } catch (failure) {
      throw new Error(aiFailure(failure));
    }
  }, [workspaceId]);

  return { view, loading, error, canManage, reload: load, save };
}
